import { useRef, useState, useEffect, useCallback } from 'react'
import { clsx } from 'clsx'
import { useAuthStore } from '../../stores/authStore'
import { useChatStore } from '../../stores/chatStore'

interface AudioPlayerProps {
  src: string
  therapistType?: string
  autoPlay?: boolean
  messageId?: number // Used to track if already auto-played
  initialDuration?: number // Pre-recorded duration (for WebM blobs with Infinity duration)
}

// Track which messages have been auto-played (persisted in localStorage)
const PLAYED_STORAGE_KEY = 'jt_played_messages'

// Module-level cache to avoid re-parsing localStorage every call
let _playedCache: Set<string> | null = null

function getPlayedMessages(): Set<string> {
  if (_playedCache) return _playedCache
  try {
    const stored = localStorage.getItem(PLAYED_STORAGE_KEY)
    _playedCache = stored ? new Set(JSON.parse(stored)) : new Set()
    return _playedCache
  } catch {
    _playedCache = new Set()
    return _playedCache
  }
}

function markAsPlayed(key: string): void {
  try {
    const played = getPlayedMessages()
    played.add(key)
    // Keep only last 500 entries to avoid localStorage bloat
    const arr = Array.from(played)
    if (arr.length > 500) {
      arr.splice(0, arr.length - 500)
    }
    localStorage.setItem(PLAYED_STORAGE_KEY, JSON.stringify(arr))
  } catch {
    // Ignore localStorage errors
  }
}

function hasBeenPlayed(key: string): boolean {
  return getPlayedMessages().has(key)
}

export default function AudioPlayer({ src, therapistType, autoPlay = true, messageId, initialDuration }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const blobUrlRef = useRef<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  // Use initialDuration if provided (for WebM blobs with Infinity duration)
  const [duration, setDuration] = useState(initialDuration || 0)
  const durationRef = useRef(initialDuration || 0)
  const updateDuration = useCallback((d: number) => {
    durationRef.current = d
    setDuration(d)
  }, [])
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isQueued, setIsQueued] = useState(false)
  const accessToken = useAuthStore((state) => state.accessToken)
  const playbackRate = useChatStore((s) => s.playbackRate)
  const currentlyPlayingId = useChatStore((s) => s.currentlyPlayingId)
  const enqueuePlayback = useChatStore((s) => s.enqueuePlayback)
  const startPlayback = useChatStore((s) => s.startPlayback)
  const finishPlayback = useChatStore((s) => s.finishPlayback)
  const canPlay = useChatStore((s) => s.canPlay)

  // Fetch audio with auth token and create blob URL
  // Skip fetch for blob URLs (user recordings) - use directly
  useEffect(() => {
    if (!src) return

    // Blob URLs are already local - use directly without fetching
    if (src.startsWith('blob:')) {
      setBlobUrl(src)
      setIsLoading(false)
      return
    }

    // Server URLs require auth token
    if (!accessToken) return

    let cancelled = false
    const controller = new AbortController()

    // Revoke previous blob URL if exists (only if we created it)
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current)
      blobUrlRef.current = null
    }

    const fetchAudio = async (token: string, isRetry = false) => {
      setIsLoading(true)
      setError(null)

      try {
        const apiUrl = import.meta.env.VITE_API_URL || ''
        const fullUrl = src.startsWith('/') ? `${apiUrl}${src}` : src

        const response = await fetch(fullUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        })

        // If 401 and not already retrying, try to refresh token
        if (response.status === 401 && !isRetry) {
          const refreshToken = useAuthStore.getState().refreshToken
          if (refreshToken) {
            try {
              const refreshResponse = await fetch(`${apiUrl}/api/v1/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
              })
              if (refreshResponse.ok) {
                const { access_token, refresh_token } = await refreshResponse.json()
                useAuthStore.getState().updateTokens(access_token, refresh_token)
                // Retry with new token
                return fetchAudio(access_token, true)
              }
            } catch {
              // Refresh failed, fall through to error
            }
          }
        }

        if (!response.ok) {
          throw new Error(`Failed to load audio: ${response.status}`)
        }

        const blob = await response.blob()
        if (!cancelled) {
          const url = URL.createObjectURL(blob)
          blobUrlRef.current = url
          setBlobUrl(url)
          setIsLoading(false)
        }
      } catch (err) {
        if (!cancelled && err instanceof Error && err.name !== 'AbortError') {
          console.error('Audio fetch error:', err)
          setError(err.message)
          setIsLoading(false)
        }
      }
    }

    fetchAudio(accessToken)

    return () => {
      cancelled = true
      controller.abort()
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current)
        blobUrlRef.current = null
      }
    }
  }, [src, accessToken])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => {
      // Update progress
      if (audio.duration && isFinite(audio.duration)) {
        setProgress((audio.currentTime / audio.duration) * 100)
        // Also update duration if not set yet (fallback for blob URLs)
        if (durationRef.current === 0 && audio.duration > 0) {
          updateDuration(audio.duration)
        }
      }
    }

    const handleDurationChange = () => {
      // Duration became available (works better for blob URLs)
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        updateDuration(audio.duration)
      }
    }

    const handleLoadedMetadata = () => {
      // Set duration when metadata loads
      if (audio.duration && isFinite(audio.duration)) {
        updateDuration(audio.duration)
      }
      // Apply current playback rate to new audio
      audio.playbackRate = playbackRate

      // Create unique key for this message/src combo
      const playKey = messageId ? `audio-${messageId}` : `audio-${src}`

      // Queue for autoplay instead of playing immediately
      if (autoPlay && blobUrl && messageId && !hasBeenPlayed(playKey)) {
        markAsPlayed(playKey)
        enqueuePlayback(messageId)
        setIsQueued(true)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setProgress(0)
      // Final fallback: use currentTime as duration (for webm blobs with Infinity duration)
      if ((durationRef.current === 0 || !isFinite(durationRef.current)) && audio.currentTime > 0) {
        updateDuration(audio.currentTime)
      }
      // Notify queue that playback finished
      finishPlayback()
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [blobUrl, autoPlay, playbackRate, updateDuration])

  const togglePlay = () => {
    if (audioRef.current && blobUrl) {
      if (isPlaying) {
        audioRef.current.pause()
        setIsPlaying(false)
      } else {
        // Manual play - mark as playing in queue
        if (messageId) {
          startPlayback(messageId)
        }
        audioRef.current.play()
        setIsPlaying(true)
      }
    }
  }

  // Sync audio element playback rate when it changes in store
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate
    }
  }, [playbackRate])

  // Check if it's our turn to play from the queue
  useEffect(() => {
    if (!isQueued || !messageId || !blobUrl || !audioRef.current) return
    if (isPlaying) return // Already playing

    // Check if we can play now
    if (canPlay(messageId)) {
      startPlayback(messageId)
      setIsQueued(false)
      audioRef.current.play().then(() => {
        setIsPlaying(true)
      }).catch((err) => {
        console.log('Autoplay blocked:', err.message)
        finishPlayback() // Release queue on error
      })
    }
  }, [isQueued, messageId, blobUrl, currentlyPlayingId, canPlay, startPlayback, finishPlayback, isPlaying])

  const formatTime = (seconds: number, showPlaceholder: boolean = false) => {
    // Guard against NaN and Infinity
    if (!isFinite(seconds) || isNaN(seconds) || seconds === 0) {
      return showPlaceholder ? '—:——' : '0:00'
    }
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const accentColor = therapistType
    ? {
        wendy: 'bg-wendy',
        artem: 'bg-artem',
        finch: 'bg-finch',
        una: 'bg-una',
      }[therapistType] || 'bg-primary-500'
    : 'bg-primary-500'

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-3 p-3 bg-white/80 rounded-2xl border border-neutral-200/50">
        <div className={clsx('w-11 h-11 rounded-full flex items-center justify-center shadow-lg', accentColor, 'opacity-70')}>
          <svg className="w-6 h-6 animate-spin text-white" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
        <div className="flex-1 text-sm text-neutral-500 font-medium">Загрузка...</div>
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center gap-3 p-2 bg-red-50 rounded-2xl">
        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-red-200">
          <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1 text-sm text-red-600">Audio unavailable</div>
      </div>
    )
  }

  // Text color for accent - use dark text on light accents
  const textColorClass = therapistType
    ? {
        wendy: 'text-wendy-dark',
        artem: 'text-artem-dark',
        finch: 'text-finch-dark',
        una: 'text-una-dark',
      }[therapistType] || 'text-primary-700'
    : 'text-primary-700'

  return (
    <div data-testid="audio-player" className="flex items-center gap-3 p-3 bg-white/80 rounded-2xl border border-neutral-200/50">
      {blobUrl && <audio ref={audioRef} src={blobUrl} preload="metadata" />}

      {/* Play/Pause button - larger with shadow */}
      <button
        data-testid="audio-play-btn"
        aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
        onClick={togglePlay}
        disabled={!blobUrl}
        className={clsx(
          'w-11 h-11 rounded-full flex items-center justify-center',
          'transition-all duration-200 active:scale-95',
          accentColor,
          'text-white shadow-lg',
          'ring-2 ring-white/50',
          !blobUrl && 'opacity-50 cursor-not-allowed'
        )}
      >
        {isPlaying ? (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        ) : (
          <svg className="w-6 h-6 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* Progress bar - more visible track */}
      <div className="flex-1">
        <div className="h-2 bg-neutral-300 rounded-full overflow-hidden">
          <div
            className={clsx('h-full rounded-full transition-all', accentColor)}
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className={clsx('flex justify-between mt-1 text-xs font-medium', textColorClass)}>
          <span>{formatTime((progress / 100) * duration)}</span>
          <span>{formatTime(duration, true)}</span>
        </div>
      </div>
    </div>
  )
}
